const TEXT_FIELD_TYPES = new Set([
  "text",
  "email",
  "tel",
  "password",
  "textarea",
  "search",
  "url",
  "select",
  "options",
  "multioptions",
  "switch",
  "file",
  "dropzone",
]);

export const isTextFieldType = (type) => {
  if (type == null || type === "") return false;
  return TEXT_FIELD_TYPES.has(String(type).toLowerCase().trim());
};

const IDENTITY_SKIP_KEY_PATTERN =
  /(^|_)(id|uuid|year|phone|telp|telepon|telephone|whatsapp|password|pin|otp|nopol|plate)(_|$)|plate_number|car_code|gsabis|_by$/i;

const CURRENCY_FIELD_KEY_PATTERN =
  /(price|cost(?!_center)|selling|buying|profit|income|amount|salary|fare|tarif|expense|revenue|toll|fee|freight)|(^|_)rate(_|$)|_rate_|rate_/i;

const NON_CURRENCY_RATE_KEY_PATTERN =
  /^rate_(characteristic|display|type|name|code)/i;

export const isCurrencyFieldKey = (columnKey) => {
  if (!columnKey) return false;
  const key = String(columnKey);
  if (NON_CURRENCY_RATE_KEY_PATTERN.test(key)) return false;
  return CURRENCY_FIELD_KEY_PATTERN.test(key);
};

const SKIP_NUMBER_FORMAT_KEY_PATTERN = IDENTITY_SKIP_KEY_PATTERN;

export const parseNumericValue = (value) => {
  if (value === null || value === undefined || value === "") return null;

  if (typeof value === "bigint") {
    const asNumber = Number(value);
    return Number.isFinite(asNumber) ? asNumber : null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^-?\d+$/.test(trimmed)) return Number(trimmed);
  if (/^-?\d+\.\d+$/.test(trimmed)) return Number(trimmed);

  return null;
};

export const shouldSkipNumberFormat = (columnKey) => {
  if (!columnKey) return false;
  const key = String(columnKey);
  if (SKIP_NUMBER_FORMAT_KEY_PATTERN.test(key)) return true;
  return isCurrencyFieldKey(key);
};

const normalizeOptions = (options) => {
  if (typeof options === "number") {
    return { fractionDigits: options };
  }
  return options && typeof options === "object" ? options : {};
};

export const formatNumber = (value, options = {}) => {
  const {
    fractionDigits,
    minFractionDigits = 0,
    emptyValue,
    columnKey = "",
    fieldType,
    columnType,
    force = false,
  } = normalizeOptions(options);

  if (value === null || value === undefined || value === "") {
    return emptyValue !== undefined ? emptyValue : (value ?? "");
  }

  if (!force && isTextFieldType(fieldType || columnType)) {
    return value;
  }

  if (!force && shouldSkipNumberFormat(columnKey)) {
    return value;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return emptyValue !== undefined ? emptyValue : value;
    if (/[%$€]|[a-zA-Z]/.test(trimmed)) return value;
    if (/^\s*rp\b/i.test(trimmed)) return value;
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return value;
  }

  const numeric = parseNumericValue(value);
  if (numeric === null) return value;

  if (!force && Number.isInteger(numeric) && Math.abs(numeric) >= 1e11) {
    return value;
  }

  const hasExplicitDigits =
    fractionDigits !== undefined && fractionDigits !== null;
  const abs = Math.abs(numeric);

  if (abs < 1000 && !hasExplicitDigits) {
    return Number.isInteger(numeric) ? String(numeric) : String(value);
  }

  const maxFractionDigits = hasExplicitDigits
    ? fractionDigits
    : Number.isInteger(numeric)
      ? 0
      : 2;

  return numeric.toLocaleString("id-ID", {
    minimumFractionDigits: hasExplicitDigits ? minFractionDigits : 0,
    maximumFractionDigits: maxFractionDigits,
  });
};

export const parseFormattedNumber = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "bigint") return Number(value);

  const trimmed = String(value).trim();
  if (!trimmed || trimmed === "-" || trimmed === ",") return null;

  const negative = trimmed.startsWith("-");
  const raw = (negative ? trimmed.slice(1) : trimmed).replace(/\s/g, "");

  if (raw.includes(",")) {
    const [intRaw, ...rest] = raw.split(",");
    const intPart = intRaw.replace(/\./g, "").replace(/\D/g, "");
    const decPart = rest.join("").replace(/\D/g, "");
    if (intPart === "" && decPart === "") return null;
    const numeric = Number(`${intPart || "0"}.${decPart}`);
    if (!Number.isFinite(numeric)) return null;
    return negative ? -numeric : numeric;
  }

  if (/^\d{1,3}(\.\d{3})+$/.test(raw)) {
    const numeric = Number(raw.replace(/\./g, ""));
    return negative ? -numeric : numeric;
  }

  if (/^\d+\.\d+$/.test(raw)) {
    const numeric = Number(raw);
    return Number.isFinite(numeric) ? (negative ? -numeric : numeric) : null;
  }

  const digits = raw.replace(/\D/g, "");
  if (!digits) return null;
  const numeric = Number(digits);
  if (!Number.isFinite(numeric)) return null;
  return negative ? -numeric : numeric;
};

export const maskNumberInput = (raw) => {
  const source = raw === null || raw === undefined ? "" : String(raw);
  const negative = source.trim().startsWith("-");
  const cleaned = source.replace(/[^\d,]/g, "");
  const commaIndex = cleaned.indexOf(",");
  const hasComma = commaIndex >= 0;
  const intDigits = (
    hasComma ? cleaned.slice(0, commaIndex) : cleaned
  ).replace(/\D/g, "");
  const decDigits = hasComma
    ? cleaned.slice(commaIndex + 1).replace(/\D/g, "")
    : "";

  const intFormatted = intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  let display = intFormatted;
  if (hasComma) display += `,${decDigits}`;
  if (negative && (intDigits || hasComma)) display = `-${display}`;
  else if (negative && !intDigits && !hasComma) display = "-";

  let emitValue = "";
  if (intDigits || decDigits) {
    const signedInt = `${negative ? "-" : ""}${intDigits || "0"}`;
    emitValue =
      hasComma && decDigits !== ""
        ? Number(`${signedInt}.${decDigits}`)
        : Number(signedInt);
    if (!Number.isFinite(emitValue)) emitValue = "";
  }

  return { display, emitValue };
};

export const formatNumberInput = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (value === "-") return "-";
  const numeric = parseFormattedNumber(value);
  if (numeric === null) return "";
  return maskNumberInput(String(numeric).replace(".", ",")).display;
};

export const formatNumberWithUnit = (value, unit = "", options = {}) => {
  const formatted = formatNumber(value, options);
  if (
    formatted === null ||
    formatted === undefined ||
    formatted === "" ||
    formatted === (options?.emptyValue ?? undefined)
  ) {
    return formatted;
  }

  return unit ? `${formatted}${unit}` : formatted;
};
